import { DndContext, DragOverlay, PointerSensor, pointerWithin, TouchSensor, useSensor, useSensors } from '@dnd-kit/core'
import { router } from '@inertiajs/react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import toast, { Toaster } from 'react-hot-toast'
import { KitchenHead } from './Components/KitchenHead'
import { KitchenHeader } from './Components/KitchenHeader'
import { LockScreen } from './Components/LockScreen'
import { OrderCard } from './Components/OrderCard'
import { OrderColumn } from './Components/OrderColumn'
import { useAudioNotifications } from './hooks/useAudioNotifications'
import { useInactivityLock } from './hooks/useInactivityLock'
import { useKitchenChannel } from './hooks/useKitchenChannel'
import { COLUMN_STATUS_MAP, STATUS_TO_COLUMN_MAP } from './utils/constants'

export default function Dashboard({ account, accounts, orders }) {
  const [activeOrder, setActiveOrder] = useState(null)
  const [optimisticMoves, setOptimisticMoves] = useState({})
  const [pendingOrders, setPendingOrders] = useState(new Set())
  const ordersRef = useRef(orders)
  const optimisticMovesRef = useRef(optimisticMoves)

  // Keep refs updated for use in intervals
  useEffect(() => {
    ordersRef.current = orders
    optimisticMovesRef.current = optimisticMoves
  }, [orders, optimisticMoves])

  // Helper to get effective status (real + optimistic)
  const getEffectiveStatus = useCallback((order) => optimisticMoves[order.id] ?? order.status, [optimisticMoves])

  // Group orders by effective status (accounts for optimistic moves)
  const pending = orders.filter(o => {
    const status = getEffectiveStatus(o)
    return status === 'paid' || status === 'confirmed'
  })
  const preparing = orders.filter(o => getEffectiveStatus(o) === 'preparing')
  const ready = orders.filter(o => getEffectiveStatus(o) === 'ready')

  // Get unactioned orders for reminder sound
  const getUnactionedOrders = useCallback(() => {
    const currentOrders = ordersRef.current
    const currentMoves = optimisticMovesRef.current
    return currentOrders.filter(o => {
      const status = currentMoves[o.id] ?? o.status
      return status === 'paid' || status === 'confirmed'
    })
  }, [])

  // Hooks
  const { audioEnabled, enableAudio, disableAudio } = useAudioNotifications({
    account,
    orders,
    getUnactionedOrders
  })

  const {
    isLocked,
    setIsLocked,
    unlockInput,
    shakeError,
    handleUnlockDigit,
    handleUnlockClear,
    handleUnlockBackspace,
    handleScreenTouch
  } = useInactivityLock({ unlockCode: account.kitchen_unlock_code })

  // Configure drag sensors
  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 8 }
    }),
    useSensor(TouchSensor, {
      activationConstraint: { delay: 250, tolerance: 5 }
    })
  )

  // Real-time updates via ActionCable + polling fallback
  const reload = useCallback(() => router.reload({ only: ['orders'] }), [])

  useKitchenChannel({ accountId: account.id, onOrdersChanged: reload })

  useEffect(() => {
    // Fallback poll in case WebSocket disconnects
    const interval = setInterval(reload, 15000)

    // Handle device wake / tab refocus
    const handleVisibility = () => {
      if (document.visibilityState === 'visible') reload()
    }
    document.addEventListener('visibilitychange', handleVisibility)
    window.addEventListener('focus', reload)

    return () => {
      clearInterval(interval)
      document.removeEventListener('visibilitychange', handleVisibility)
      window.removeEventListener('focus', reload)
    }
  }, [reload])

  const updateStatus = async (orderNumber, newStatus, previousStatus = null) => {
    try {
      const response = await fetch(`/kitchen/orders/${orderNumber}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (!response.ok) {
        throw new Error('Failed to update order status')
      }

      router.reload({ only: ['orders'] })

      if (newStatus === 'collected' && previousStatus) {
        toast.success(
          (t) => (
            <div className="flex items-center gap-3">
              <span className="flex-1">Order #{orderNumber} marked as collected</span>
              <button
                onClick={() => {
                  toast.dismiss(t.id)
                  updateStatus(orderNumber, previousStatus)
                }}
                className="bg-white text-gray-900 px-3 py-1 rounded font-medium hover:bg-gray-100 transition"
              >
                Undo
              </button>
            </div>
          ),
          { duration: 8000, style: { minWidth: '400px' } }
        )
      }
    } catch (error) {
      console.error('Failed to update order status:', error)
      toast.error('Failed to update order status. Please try again.')
    }
  }

  const handleDragStart = (event) => {
    const order = orders.find(o => o.id === event.active.id)
    setActiveOrder(order)
  }

  const handleDragEnd = async (event) => {
    const { active, over } = event
    setActiveOrder(null)

    if (!over) return

    const orderId = active.id
    const draggedOrder = orders.find(o => o.id === orderId)
    if (!draggedOrder) return

    const targetColumn = over.id
    const newStatus = COLUMN_STATUS_MAP[targetColumn]
    const currentColumn = STATUS_TO_COLUMN_MAP[draggedOrder.status]

    if (targetColumn === currentColumn) return

    setOptimisticMoves(prev => ({ ...prev, [orderId]: newStatus }))
    setPendingOrders(prev => new Set(prev).add(orderId))

    try {
      const response = await fetch(`/kitchen/orders/${draggedOrder.order_number}/status`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
          'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
        },
        body: JSON.stringify({ status: newStatus })
      })

      if (!response.ok) {
        throw new Error('Failed to update order status')
      }

      setOptimisticMoves(prev => {
        const next = { ...prev }
        delete next[orderId]
        return next
      })

      router.reload({ only: ['orders'] })
    } catch (error) {
      setOptimisticMoves(prev => {
        const next = { ...prev }
        delete next[orderId]
        return next
      })
      console.error('Failed to update order status:', error)
      toast.error('Failed to move order')
    } finally {
      setPendingOrders(prev => {
        const next = new Set(prev)
        next.delete(orderId)
        return next
      })
    }
  }

  return (
    <div className="min-h-screen bg-gray-100">
      <KitchenHead
        account={account}
        newCount={pending.length}
        preparingCount={preparing.length}
        readyCount={ready.length}
      />

      {isLocked && (
        <LockScreen
          pending={pending.length}
          preparing={preparing.length}
          ready={ready.length}
          audioEnabled={audioEnabled}
          unlockCode={account.kitchen_unlock_code}
          unlockInput={unlockInput}
          shakeError={shakeError}
          onDigit={handleUnlockDigit}
          onClear={handleUnlockClear}
          onBackspace={handleUnlockBackspace}
          onScreenTouch={handleScreenTouch}
        />
      )}

      <Toaster
        position="top-center"
        toastOptions={{
          success: { style: { background: '#10b981', color: 'white' } },
          error: { style: { background: '#ef4444', color: 'white' } }
        }}
      />

      <div id="drag-instructions" className="sr-only">
        Press space or enter to pick up. Use arrow keys to move. Press space or enter to drop.
      </div>

      <KitchenHeader
        account={account}
        accounts={accounts}
        audioEnabled={audioEnabled}
        onToggleAudio={() => audioEnabled ? disableAudio() : enableAudio()}
        onLock={() => setIsLocked(true)}
      />

      <div className="max-w-7xl mx-auto px-4 py-6 sm:px-6 lg:px-8">
        <DndContext
          sensors={sensors}
          collisionDetection={pointerWithin}
          onDragStart={handleDragStart}
          onDragEnd={handleDragEnd}
        >
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <OrderColumn
              columnId="new"
              title="New Orders"
              count={pending.length}
              orders={pending}
              badgeColor="bg-red-100 text-red-800"
              onUpdateStatus={(orderNumber) => updateStatus(orderNumber, 'preparing')}
              buttonText="Start Preparing"
              buttonColor="bg-yellow-600 hover:bg-yellow-700"
              showUndo={false}
              isDragging={!!activeOrder}
              pendingOrders={pendingOrders}
              pulse={pending.length > 0}
              bounce={pending.length > 0}
            />

            <OrderColumn
              columnId="preparing"
              title="Preparing"
              count={preparing.length}
              orders={preparing}
              badgeColor="bg-yellow-100 text-yellow-800"
              onUpdateStatus={(orderNumber) => updateStatus(orderNumber, 'ready')}
              onUndo={(orderNumber) => updateStatus(orderNumber, 'confirmed')}
              buttonText="Mark Ready"
              buttonColor="bg-green-600 hover:bg-green-700"
              showUndo={true}
              undoText="← Back to New"
              isDragging={!!activeOrder}
              pendingOrders={pendingOrders}
            />

            <OrderColumn
              columnId="ready"
              title="Ready for Pickup"
              count={ready.length}
              orders={ready}
              badgeColor="bg-green-100 text-green-800"
              onUpdateStatus={(orderNumber) => updateStatus(orderNumber, 'collected', 'ready')}
              onUndo={(orderNumber) => updateStatus(orderNumber, 'preparing')}
              buttonText="Complete"
              buttonColor="bg-gray-600 hover:bg-gray-700"
              showUndo={true}
              undoText="← Back to Preparing"
              isDragging={!!activeOrder}
              pendingOrders={pendingOrders}
            />
          </div>

          <DragOverlay>
            {activeOrder ? (
              <div className="w-80 scale-105 rotate-2 transition-transform">
                <OrderCard
                  order={activeOrder}
                  showActions={false}
                  className="shadow-2xl border-2 border-blue-400"
                />
              </div>
            ) : null}
          </DragOverlay>
        </DndContext>
      </div>
    </div>
  )
}
